import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Map as MapIcon, Image as ImageIcon, FileText } from 'lucide-react';
import GoogleMap from './components/GoogleMap';
import AdminPanel from './components/AdminPanel';
import { getCarouselImageUrl, getHeroImageUrl } from './config/cloudfront';
import { TRIPS_DATA, fetchS3Photos } from './data/trips';
import { createApiClient } from './config/api';

// Logo Component - Globe + Compass Rose
const Logo = ({ horizontal = false }) => {
  return (
    <div className={`flex items-center gap-3 ${horizontal ? 'flex-row' : 'flex-col'}`}>
      <svg width={horizontal ? "40" : "60"} height={horizontal ? "40" : "60"} viewBox="0 0 100 100" className="flex-shrink-0">
        {/* Globe */}
        <circle cx="50" cy="50" r="40" fill="none" stroke="#3b82f6" strokeWidth="1.5"/>
        <ellipse cx="50" cy="50" rx="38" ry="12" fill="none" stroke="#3b82f6" strokeWidth="1"/>
        
        {/* Continents - soft representation */}
        <path d="M 35 35 Q 40 30 45 35 Q 42 40 35 40 Z" fill="#10b981" opacity="0.6"/>
        <path d="M 55 40 Q 65 38 68 50 Q 65 58 55 60 Z" fill="#10b981" opacity="0.6"/>
        <path d="M 30 55 Q 38 60 45 58 Q 40 65 30 68 Z" fill="#10b981" opacity="0.6"/>
        
        {/* Compass Rose - overlaid */}
        <g opacity="0.8">
          {/* North point - N from "Not" */}
          <line x1="50" y1="12" x2="50" y2="25" stroke="#10b981" strokeWidth="2"/>
          <polygon points="50,10 48,16 52,16" fill="#10b981"/>
          
          {/* Other cardinal directions - softer */}
          <line x1="88" y1="50" x2="75" y2="50" stroke="#3b82f6" strokeWidth="1.5" opacity="0.6"/>
          <line x1="50" y1="88" x2="50" y2="75" stroke="#3b82f6" strokeWidth="1.5" opacity="0.6"/>
          <line x1="12" y1="50" x2="25" y2="50" stroke="#3b82f6" strokeWidth="1.5" opacity="0.6"/>
          
          {/* Diagonal points */}
          <line x1="72" y1="28" x2="63" y2="37" stroke="#3b82f6" strokeWidth="1" opacity="0.4"/>
          <line x1="72" y1="72" x2="63" y2="63" stroke="#3b82f6" strokeWidth="1" opacity="0.4"/>
          <line x1="28" y1="72" x2="37" y2="63" stroke="#3b82f6" strokeWidth="1" opacity="0.4"/>
          <line x1="28" y1="28" x2="37" y2="37" stroke="#3b82f6" strokeWidth="1" opacity="0.4"/>
          
          {/* Center circle */}
          <circle cx="50" cy="50" r="4" fill="#10b981"/>
        </g>
      </svg>
      
      <div className={horizontal ? "flex flex-col" : "text-center"}>
        <h1 className="font-serif text-xl font-light tracking-wide text-gray-900">
          I Am Not Your Guide
        </h1>
      </div>
    </div>
  );
};

// Photo Carousel Component
const PhotoCarousel = ({ photos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextPhoto = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  if (!photos || photos.length === 0) {
    return (
      <div className="w-full bg-gray-100 aspect-video rounded-lg flex items-center justify-center border border-gray-200">
        <div className="text-center">
          <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No photos yet for this location</p>
        </div>
      </div>
    );
  }

  // Convert S3 URL to S3 key for CloudFront
  const getS3Key = (url) => {
    if (!url) return '';
    // If it's already a path, return it
    if (!url.includes('://')) return url;
    // Extract S3 key from full URL
    try {
      const s3Pattern = /travel-site-\d+-us-east-1-an\.s3\.us-east-1\.amazonaws\.com\/(.+)/;
      const match = url.match(s3Pattern);
      return match ? match[1] : url;
    } catch {
      return url;
    }
  };

  return (
    <div className="w-full">
      <div className="relative w-full bg-gray-100 aspect-video rounded-lg overflow-hidden">
        <img
          src={getCarouselImageUrl(getS3Key(photos[currentIndex]))}
          alt={`${currentIndex + 1}`}
          className="w-full h-full object-contain"
        />

        {/* Navigation */}
        <button
          onClick={prevPhoto}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 transition-all"
        >
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>
        <button
          onClick={nextPhoto}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 transition-all"
        >
          <ChevronRight className="w-6 h-6 text-gray-900" />
        </button>

        {/* Counter */}
        <div className="absolute bottom-4 right-4 bg-white bg-opacity-70 px-3 py-1 rounded-full text-sm text-gray-900 font-medium">
          {currentIndex + 1} / {photos.length}
        </div>
      </div>
    </div>
  );
};

// Main App
export default function App() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('photos');
  const [activeTripTab, setActiveTripTab] = useState('overview');
  const [tripsWithPhotos, setTripsWithPhotos] = useState([]);

  // Check if admin parameter is in URL
  const urlParams = new URLSearchParams(window.location.search);
  const adminFromUrl = urlParams.get('admin') === 'true';
  const [showAdmin, setShowAdmin] = useState(adminFromUrl);

  // Debug logging
  useEffect(() => {
    console.log('App mounted - showAdmin:', showAdmin, 'adminFromUrl:', adminFromUrl);
  }, [showAdmin, adminFromUrl]);

  // Fetch trips from API and photos from S3 when component mounts
  useEffect(() => {
    const loadTripsAndPhotos = async () => {
      try {
        const api = createApiClient();

        // First, try to fetch trips from the API
        let trips = [];
        try {
          trips = await api.listTrips();
        } catch (error) {
          console.warn('Failed to fetch trips from API, using hardcoded data:', error);
          trips = TRIPS_DATA;
        }

        // For each trip, fetch destinations and photos
        const updatedTrips = await Promise.all(
          trips.map(async (trip) => {
            let locations = trip.locations || [];

            // If locations not in trip object, try to fetch from API
            if (!locations || locations.length === 0) {
              try {
                locations = await api.listDestinations(trip.id);
              } catch (error) {
                console.warn(`Failed to fetch destinations for trip ${trip.id}:`, error);
                // If API fails, try to find in hardcoded data
                const hardcodedTrip = TRIPS_DATA.find(t => t.id === trip.id);
                locations = hardcodedTrip?.locations || [];
              }
            }

            // Fetch photos from S3 for each location
            const updatedLocations = await Promise.all(
              locations.map(async (location) => {
                const folder = location.s3FolderName || location.id;
                const photos = await fetchS3Photos(folder);
                return { ...location, photos };
              })
            );

            return { ...trip, locations: updatedLocations };
          })
        );

        setTripsWithPhotos(updatedTrips);
      } catch (error) {
        console.error('Error loading trips and photos:', error);
        // Fallback to hardcoded data with photos
        const updatedTrips = await Promise.all(
          TRIPS_DATA.map(async (trip) => {
            const updatedLocations = await Promise.all(
              trip.locations.map(async (location) => {
                const folder = location.s3FolderName || location.id;
                const photos = await fetchS3Photos(folder);
                return { ...location, photos };
              })
            );
            return { ...trip, locations: updatedLocations };
          })
        );
        setTripsWithPhotos(updatedTrips);
      }
    };

    loadTripsAndPhotos();
  }, []);

// Get a random hero image from your S3 bucket
  const [heroImage, setHeroImage] = useState('');

  useEffect(() => {
    const getRandomHeroImage = async () => {
      // All your location folders
      const folders = [
        '001home_and_cooks',
        '002new_zealand',
        '003australia',
        '004nepal',
        '005thailand',
        '006laos',
        '007vietnam',
        '008cambodia',
        '009thailand_2nd_time',
        '010india',
        '011Tanzania',
        '012Kenya',
        '013Mauritius',
        '014SouthAfrica',
        '015Amsterdam',
      ];
      
      // Pick a random folder
      const randomFolder = folders[Math.floor(Math.random() * folders.length)];
      
      // Fetch photos from that folder
      const photos = await fetchS3Photos(randomFolder);
      
      if (photos.length > 0) {
        // Pick a random photo from that folder
        const randomPhoto = photos[Math.floor(Math.random() * photos.length)];
        setHeroImage(randomPhoto);
      }
    };
    
    getRandomHeroImage();
  }, []);

  const randomHero = heroImage;

   const navigateToTrips = () => {
    setCurrentView('trips');
  };

  const navigateToTrip = (trip) => {
    setSelectedTrip(trip);
    setActiveTripTab('overview');
    setCurrentView('trip-detail');
  };

  const navigateToLocation = (location) => {
    setSelectedLocation(location);
    setActiveTab('photos');
    setCurrentView('location-detail');
  };

  const navigateHome = () => {
    setCurrentView('home');
    setSelectedTrip(null);
    setSelectedLocation(null);
  };

  // Render AdminPanel as overlay if showAdmin is true
  if (showAdmin) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <AdminPanel onClose={() => setShowAdmin(false)} />
        </div>
      </div>
    );
  }

  // HOME VIEW
  if (currentView === 'home') {
    const mostRecentTrip = tripsWithPhotos[0];
    return (
      <div className="min-h-screen bg-white">
        <header className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <button onClick={navigateHome} className="cursor-pointer">
            <Logo horizontal={true} />
          </button>
          <nav className="flex gap-6 text-sm text-gray-700 items-center">
            <button onClick={navigateToTrips} className="hover:text-gray-900 transition">Trips</button>
          </nav>
        </header>

        <div className="relative w-full h-96 bg-gray-100 overflow-hidden">
          <img
            src={randomHero.includes('unsplash') ? randomHero : getHeroImageUrl(randomHero.replace(/https:\/\/travel-site-\d+-us-east-1-an\.s3\.us-east-1\.amazonaws\.com\//, ''))}
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent"></div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-12">
          <h2 className="text-4xl font-light font-serif text-gray-900 mb-4">Welcome</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
          </p>
          <p className="text-gray-600 leading-relaxed mb-12">
            Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
          </p>

          <div className="flex gap-4">
            {mostRecentTrip ? (
              <button
                onClick={() => navigateToTrip(mostRecentTrip)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Latest Trip: {mostRecentTrip.title}
              </button>
            ) : (
              <button
                className="px-6 py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed font-medium opacity-50"
                disabled
              >
                Latest Trip: Loading...
              </button>
            )}
            <button
              onClick={navigateToTrips}
              className="px-6 py-3 border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              All Trips
            </button>
          </div>
        </div>
      </div>
    );
  }

  // TRIPS VIEW
  if (currentView === 'trips') {
    return (
      <div className="min-h-screen bg-white">
        <header className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <button onClick={navigateHome} className="cursor-pointer">
            <Logo horizontal={true} />
          </button>
          <nav className="flex gap-6 text-sm text-gray-700">
            <button onClick={navigateToTrips} className="font-medium text-gray-900">Trips</button>
          </nav>
        </header>

        <div className="max-w-3xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-light font-serif text-gray-900 mb-12">All Trips</h1>

          <div className="space-y-6">
            {tripsWithPhotos.map((trip) => (
              <button
                key={trip.id}
                onClick={() => navigateToTrip(trip)}
                className="w-full text-left p-6 border border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-serif text-gray-900 mb-2">{trip.title}</h2>
                    <p className="text-gray-600">{trip.description}</p>
                  </div>
                  <span className="text-gray-500 text-sm font-medium">{trip.date}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // TRIP DETAIL VIEW
  if (currentView === 'trip-detail' && selectedTrip) {
    return (
      <div className="min-h-screen bg-white">
        <header className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <button onClick={navigateHome} className="cursor-pointer">
            <Logo horizontal={true} />
          </button>
          <nav className="flex gap-6 text-sm text-gray-700 items-center">
            <button onClick={navigateToTrips} className="hover:text-gray-900 transition">Trips</button>
          </nav>
        </header>

        <div className="max-w-4xl mx-auto px-6 py-12">
          <button
            onClick={navigateToTrips}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium mb-6"
          >
            ← Back to Trips
          </button>

          <h1 className="text-4xl font-light font-serif text-gray-900 mb-8">{selectedTrip.title}</h1>

          <div className="flex gap-8 border-b border-gray-200 mb-8">
            <button
              onClick={() => setActiveTripTab('overview')}
              className={`pb-4 font-medium transition ${
                activeTripTab === 'overview'
                  ? 'text-gray-900 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <MapIcon className="w-4 h-4" /> Overview
              </span>
            </button>
            {selectedTrip.aboutText && (
              <button
                onClick={() => setActiveTripTab('about')}
                className={`pb-4 font-medium transition ${
                  activeTripTab === 'about'
                    ? 'text-gray-900 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" /> About
                </span>
              </button>
            )}
          </div>

{activeTripTab === 'overview' && (
  <div>
    <div className="mb-12">
      <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
        <MapPin className="w-5 h-5" /> Trip Route ({selectedTrip.locations.length} locations)
      </h2>
      {selectedTrip.locations && selectedTrip.locations.length > 0 ? (
        <GoogleMap 
          locations={selectedTrip.locations.map(loc => ({
            name: loc.title,
            lat: loc.coordinates.lat,
            lng: loc.coordinates.lng,
          }))}
          showPolyline={true}
        />
      ) : (
        <div className="text-gray-500">Map coming soon</div>
      )}
    </div>
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">Locations</h2>
                <div className="grid gap-4">
                  {selectedTrip.locations.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => navigateToLocation(location)}
                      className="p-6 border border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition text-left"
                    >
                      <h3 className="text-xl font-serif text-gray-900 mb-2">{location.title}</h3>
                      <p className="text-gray-600 text-sm">{location.photos?.length || 0} photos</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTripTab === 'about' && selectedTrip.aboutText && (
            <div className="max-w-2xl">
              <h2 className="text-2xl font-serif text-gray-900 mb-4">About This Trip</h2>
              <p className="text-gray-700 leading-relaxed">
                {selectedTrip.aboutText}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // LOCATION DETAIL VIEW
  if (currentView === 'location-detail' && selectedLocation && selectedTrip) {
    return (
      <div className="min-h-screen bg-white">
        <header className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <button onClick={navigateHome} className="cursor-pointer">
            <Logo horizontal={true} />
          </button>
          <nav className="flex gap-6 text-sm text-gray-700 items-center">
            <button onClick={navigateToTrips} className="hover:text-gray-900 transition">Trips</button>
          </nav>
        </header>

        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="flex gap-6 mb-8">
            <div>
              <button
                onClick={() => setCurrentView('trip-detail')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                ← Back to {selectedTrip.title}
              </button>
              <h1 className="text-4xl font-light font-serif text-gray-900 mt-4">{selectedLocation.title}</h1>
            </div>
          </div>

          <div className="flex gap-8 border-b border-gray-200 mb-8">
            <button
              onClick={() => setActiveTab('photos')}
              className={`pb-4 font-medium transition ${
                activeTab === 'photos'
                  ? 'text-gray-900 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Photos
              </span>
            </button>
            <button
              onClick={() => setActiveTab('about')}
              className={`pb-4 font-medium transition ${
                activeTab === 'about'
                  ? 'text-gray-900 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" /> About
              </span>
            </button>
          </div>

          {activeTab === 'photos' && (
            <div>
              <PhotoCarousel photos={selectedLocation.photos} />
              <p className="text-gray-500 text-sm mt-4">
                Use arrow keys or buttons to navigate through photos
              </p>
            </div>
          )}

          {activeTab === 'about' && (
            <div className="max-w-2xl">
              <h2 className="text-2xl font-serif text-gray-900 mb-4">About This Location</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                {selectedLocation.description}
              </p>

              {selectedLocation.importantSpots && selectedLocation.importantSpots.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5" /> Important Spots
                  </h3>
                  <GoogleMap locations={selectedLocation.importantSpots} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Should never reach here, but return null as fallback
  return null;
}

